import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@state/rootReducer";
import { useAppDispatch } from "@state/store";
import {
  fetchEisFundInvestments,
  loadMoreEisFundInvestments,
  selectCommitmentCache,
  selectIsCacheValid,
} from "@state/eisFund";
import { EisFundStartup, InvestmentStatus, TaxBenefit } from "./types";

interface UseEisFundInvestmentsOptions {
  dealId: string;
  commitmentId: string;
  dealName?: string;
  taxBenefit?: TaxBenefit | "all";
  statusLabelNames?: InvestmentStatus[];
  pageSize?: number;
  /** Skip fetching if true */
  skip?: boolean;
}

interface UseEisFundInvestmentsReturn {
  /** List of startup investments */
  investments: EisFundStartup[];
  /** Loading initial data */
  isLoading: boolean;
  /** Loading more data (pagination) */
  isLoadingMore: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether there are more items to load */
  hasNextPage: boolean;
  /** Total count of investments */
  totalCount: number;
  /** Load next page of results */
  loadMore: () => void;
  /** Force refetch (bypasses cache) */
  refetch: () => void;
}

/**
 * Hook for fetching EIS Fund investments with infinite scroll pagination.
 * Uses Redux for caching to avoid refetching on navigation.
 *
 * Uses the API endpoint: GET /investments/eis-fund/{commitmentId}
 *
 * @example
 * const { investments, isLoading, hasNextPage, loadMore } = useEisFundInvestments({
 *   dealId: 'deal-123',
 *   commitmentId: 'abc123',
 *   dealName: 'Tech',
 *   taxBenefit: 'EIS',
 *   statusLabelNames: ['NEW INVESTMENT', 'BREAKOUT POTENTIAL'],
 * });
 */
export function useEisFundInvestments({
  dealId,
  commitmentId,
  dealName,
  taxBenefit,
  statusLabelNames,
  pageSize = 10,
  skip = false,
}: UseEisFundInvestmentsOptions): UseEisFundInvestmentsReturn {
  const dispatch = useAppDispatch();

  const cache = useSelector((state: RootState) =>
    selectCommitmentCache(state, dealId, commitmentId)
  );

  const isCacheValid = useSelector((state: RootState) =>
    selectIsCacheValid(state, dealId, commitmentId, dealName, taxBenefit, statusLabelNames)
  );

  useEffect(() => {
    if (skip || !dealId || !commitmentId) return;

    if (!isCacheValid) {
      dispatch(
        fetchEisFundInvestments({
          dealId,
          commitmentId,
          dealName,
          taxBenefit,
          statusLabelNames,
          pageSize,
        })
      );
    }
  }, [
    dispatch,
    dealId,
    commitmentId,
    dealName,
    taxBenefit,
    JSON.stringify(statusLabelNames),
    isCacheValid,
    pageSize,
    skip,
  ]);

  // Load more handler for infinite scroll
  const loadMore = useCallback(() => {
    const isLoadingMore = cache.status === "loadingMore";
    if (isLoadingMore || !cache.pagination.hasNextPage) return;

    dispatch(
      loadMoreEisFundInvestments({
        dealId,
        commitmentId,
        dealName,
        taxBenefit,
        statusLabelNames,
        pageSize,
        page: cache.pagination.currentPage + 1,
      })
    );
  }, [
    dispatch,
    dealId,
    commitmentId,
    dealName,
    taxBenefit,
    JSON.stringify(statusLabelNames),
    pageSize,
    cache.status,
    cache.pagination.hasNextPage,
    cache.pagination.currentPage,
  ]);

  // Manual refetch handler (bypasses cache)
  const refetch = useCallback(() => {
    dispatch(
      fetchEisFundInvestments({
        dealId,
        commitmentId,
        dealName,
        taxBenefit,
        statusLabelNames,
        pageSize,
      })
    );
  }, [
    dispatch,
    dealId,
    commitmentId,
    dealName,
    taxBenefit,
    JSON.stringify(statusLabelNames),
    pageSize,
  ]);

  return {
    investments: cache.investments,
    isLoading: cache.status === "loading",
    isLoadingMore: cache.status === "loadingMore",
    error: cache.error,
    hasNextPage: cache.pagination.hasNextPage,
    totalCount: cache.pagination.totalCount,
    loadMore,
    refetch,
  };
}

export default useEisFundInvestments;
